import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ParentPlan, ParentPlanService, Plan, PlanService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const FAQ_KEYS = ['rates', 'pay', 'commission', 'autoRenew', 'cancel'] as const;

type PlanCard = Plan & {
  superscript: string;
  ctaLabelKey: string;
  primaryTitleKey: string;
};

const getSuperscript = (frequency?: string): string => {
  if (frequency === 'monthly') return '/mo';
  if (frequency === 'yearly') return '/yr';
  if (frequency === 'quarterly') return '/qtr';
  return '';
};

const enhance = (plan: Plan): PlanCard => ({
  ...plan,
  superscript: getSuperscript(plan.frequency),
  ctaLabelKey: plan.frequency === 'single' ? 'pages.parentPricing.buyPass' : 'pages.parentPricing.subscribe',
  primaryTitleKey: plan.frequency === 'single' ? 'pages.parentPricing.noCommitment' : 'pages.parentPricing.subscribe',
});

export const ParentPricing = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [plans, setPlans] = useState<PlanCard[] | null>(null);
  const [activePlan, setActivePlan] = useState<boolean>(false);
  const [openFaq, setOpenFaq] = useState<Record<string, boolean>>({});

  useEffect(() => {
    document.title = 'UrbanSitter - Pricing';
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    const loadPlans = async (): Promise<void> => {
      try {
        const resp = await PlanService.getByFilter({ params: { limit: 3, parentId: user.id, userId: user.id } });
        const loaded = resp.data?.data ?? [];
        // Display order mirrors Angular parent-pricing.js:64-66 — the primary
        // slot is the single (one-time) pass labeled "No Commitment", and the
        // secondary/tertiary pair is the annual + quarterly subscriptions
        // shown together under "Subscribe & Save".
        const single = loaded.find((p) => p.frequency === 'single');
        const annual = loaded.find((p) => p.frequency === 'yearly');
        const quarterly = loaded.find((p) => p.frequency === 'quarterly');
        setPlans(
          [single, annual, quarterly]
            .filter((p): p is Plan => !!p)
            .map(enhance),
        );
      } catch {
        setPlans([]);
      }
    };

    const loadActivePlan = async (): Promise<void> => {
      try {
        const resp = await ParentPlanService.getByFilter({ params: { userId: user.id, parentId: user.id, active: true } });
        const list = (resp.data?.data ?? []) as ParentPlan[];
        setActivePlan(list.length > 0);
      } catch {
        setActivePlan(false);
      }
    };

    void loadPlans();
    void loadActivePlan();
  }, [user?.id]);

  const primary = plans?.[0];
  const secondary = plans?.[1];
  const tertiary = plans?.[2];

  const goToBuyMembership = (planId?: number): void => {
    if (!user?.id) return;
    const q = planId ? `?planId=${planId}` : '';
    navigate(`/parent/${user.id}/buy-membership${q}`);
  };

  const toggle = (key: string): void => setOpenFaq((s) => ({ ...s, [key]: !s[key] }));

  const faqItems = useMemo(
    () =>
      FAQ_KEYS.map((key) => ({
        key,
        title: t(`pages.parentPricing.faq.${key}.title`),
        content: t(`pages.parentPricing.faq.${key}.content`),
      })),
    [t],
  );

  return (
    <>
      <section className='container py-10 max-w-5xl mx-auto text-center'>
        <h1 className='text-3xl font-semibold mb-4'>{t('pages.parentPricing.heading')}</h1>
        <p className='text-base mb-8 max-w-2xl mx-auto'>{t('pages.parentPricing.subheading')}</p>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          {primary && (
            <PlanBox
              plan={primary}
              title={t(primary.primaryTitleKey)}
              hideCta={activePlan}
              onCta={(): void => goToBuyMembership(primary.id)}
              tCta={t(primary.ctaLabelKey)}
              accent='secondary'
            />
          )}

          {secondary && tertiary ? (
            <>
              <div className='hidden md:block md:col-span-2 bg-white rounded overflow-hidden border border-sapphire'>
                <div className='py-2 text-base font-medium bg-sapphire text-white'>
                  {t('pages.parentPricing.subscribeAndSave')}
                </div>
                <div className='flex items-center'>
                  <div className='flex-1 p-4'>
                    <PlanContent
                      plan={secondary}
                      hideCta={activePlan}
                      onCta={(): void => goToBuyMembership(secondary.id)}
                      tCta={t(secondary.ctaLabelKey)}
                      accent='primary'
                    />
                  </div>
                  <div className='font-bold text-neutral-700 px-2' aria-hidden='true'>
                    - OR -
                  </div>
                  <div className='flex-1 p-4'>
                    <PlanContent
                      plan={tertiary}
                      hideCta={activePlan}
                      onCta={(): void => goToBuyMembership(tertiary.id)}
                      tCta={t(tertiary.ctaLabelKey)}
                      accent='primary'
                    />
                  </div>
                </div>
              </div>

              <div className='md:hidden'>
                <PlanBox
                  plan={secondary}
                  title={t('pages.parentPricing.subscribeAndSave')}
                  hideCta={activePlan}
                  onCta={(): void => goToBuyMembership(secondary.id)}
                  tCta={t(secondary.ctaLabelKey)}
                  accent='primary'
                />
              </div>
              <div className='md:hidden'>
                <PlanBox
                  plan={tertiary}
                  title={t('pages.parentPricing.subscribeAndSave')}
                  hideCta={activePlan}
                  onCta={(): void => goToBuyMembership(tertiary.id)}
                  tCta={t(tertiary.ctaLabelKey)}
                  accent='primary'
                />
              </div>
            </>
          ) : (
            <>
              {secondary && (
                <PlanBox
                  plan={secondary}
                  title={t('pages.parentPricing.subscribeAndSave')}
                  hideCta={activePlan}
                  onCta={(): void => goToBuyMembership(secondary.id)}
                  tCta={t(secondary.ctaLabelKey)}
                  accent='primary'
                />
              )}
              {tertiary && (
                <PlanBox
                  plan={tertiary}
                  title={t('pages.parentPricing.subscribeAndSave')}
                  hideCta={activePlan}
                  onCta={(): void => goToBuyMembership(tertiary.id)}
                  tCta={t(tertiary.ctaLabelKey)}
                  accent='primary'
                />
              )}
            </>
          )}
        </div>
      </section>

      <section className='bg-white py-10'>
        <div className='container max-w-2xl mx-auto'>
          <h2 className='text-2xl font-semibold text-center mb-6'>{t('pages.parentPricing.faqHeading')}</h2>
          <hr className='mb-4' />
          {faqItems.map((q) => (
            <div key={q.key}>
              <Button
                buttonType={ButtonType.tertiary}
                onClick={(): void => toggle(q.key)}
                className='w-full text-left flex items-center justify-between py-3'
              >
                <span className='text-lg font-medium'>{q.title}</span>
                <span className='text-2xl leading-none'>{openFaq[q.key] ? '−' : '+'}</span>
              </Button>
              {openFaq[q.key] && <p className='pb-3 text-neutral-700'>{q.content}</p>}
              <hr />
            </div>
          ))}
        </div>
      </section>
    </>
  );
});

interface PlanContentProps {
  plan: PlanCard;
  tCta: string;
  hideCta: boolean;
  onCta: () => void;
  accent: 'primary' | 'secondary';
}

const PlanContent = ({ plan, tCta, hideCta, onCta, accent }: PlanContentProps): JSX.Element => (
  <>
    <div className='text-lg font-semibold'>{plan.name}</div>
    {plan.description && (
      <div className={`font-semibold ${accent === 'primary' ? 'text-sapphire' : 'text-green-700'}`}>
        {plan.description}
      </div>
    )}
    <hr className='my-3' />
    <div className='font-bold'>
      <span className='text-3xl'>${plan.fee}</span>
      <span className='text-xl'>{plan.superscript}</span>
    </div>
    {!hideCta && (
      <Button buttonType={ButtonType.primary} onClick={onCta} className='mt-4'>
        {tCta}
      </Button>
    )}
  </>
);

interface PlanBoxProps extends PlanContentProps {
  title: string;
}

const PlanBox = ({ title, accent, ...rest }: PlanBoxProps): JSX.Element => (
  <div
    className={`bg-white rounded overflow-hidden border ${
      accent === 'primary' ? 'border-sapphire' : 'border-neutral-300'
    }`}
  >
    <div
      className={`py-2 text-base font-medium ${
        accent === 'primary' ? 'bg-sapphire text-white' : 'bg-neutral-200 text-neutral-900'
      }`}
    >
      {title}
    </div>
    <div className='p-4'>
      <PlanContent accent={accent} {...rest} />
    </div>
  </div>
);
